import Store from '@ember-data/store';
import Route from '@ember/routing/route';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import { dasherize } from '@ember/string';
import DashboardModel from 'donor-elf-web/models/dashboard';
import FundModel from 'donor-elf-web/models/fund';
import GoalModel from 'donor-elf-web/models/goal';
import AbilitiesService from 'donor-elf-web/services/abilities';
import CampaignService from 'donor-elf-web/services/campaign-service';
import CurrentUser from 'donor-elf-web/services/current-user';
import FundsService from 'donor-elf-web/services/funds-service';
import NavbarSettingsService from 'donor-elf-web/services/navbar-settings';

export interface DashboardRouteModel {
  fund: FundModel;
  dashboard: DashboardModel;
  goal?: GoalModel;
}

// used both here and in funds/fund/dashboard/index.ts
export function RedirectToNamedDashboardRoute(args: {
  model: DashboardRouteModel;
  currentUser: CurrentUser;
  transitionTo: (route: string) => void;
}) {
  if (args.currentUser.stripeIsDelinquent) {
    args.transitionTo('funds.fund.dashboard.update-card');
  } else {
    let goalType: string = args.model.goal?.goalType || 'no_goal';
    // ***
    // duct tape fix for now
    if (goalType !== 'no_goal' && args.currentUser.organizationName.spo) {
      goalType = 'spo';
    } else if (goalType === 'ytd' && args.currentUser.organizationName.ruf) {
      goalType = 'ruf';
    } else if (goalType === 'ytd' && args.currentUser.organizationName.yl) {
      goalType = 'yl';
    } else if (
      goalType === 'rolling' &&
      args.currentUser.organizationName.eastwest
    ) {
      goalType = 'ew';
    }
    // ***
    args.transitionTo(`funds.fund.dashboard.${dasherize(goalType)}`);
  }
}

export default class DashboardRoute extends Route {
  @service declare abilities: AbilitiesService;
  @service declare campaignService: CampaignService;
  @service declare currentUser: CurrentUser;
  @service declare fundsService: FundsService;
  @service declare navbarSettings: NavbarSettingsService;
  @service declare router: RouterService;
  @service declare store: Store;

  beforeModel() {
    this.navbarSettings.title = 'Dashboard';
  }

  async model(): Promise<DashboardRouteModel> {
    let fund = this.fundsService.selectedFund!;
    let dashboard = await this.store.findRecord('dashboard', fund.id, {
      reload: true,
    });
    if (dashboard?.hasActiveJournal && !this.currentUser.organizationName.yl) {
      this.navbarSettings.showActiveJournalButton = true;
    }

    if (this.abilities.hasAbility('import_ag_usa')) {
      this.navbarSettings.importButtonRoute = 'funds.fund.import.ag-usa';
    } else if (this.abilities.hasAbility('import_ag_wm')) {
      this.navbarSettings.importButtonRoute = 'funds.fund.import.ag-wm';
    }

    this.fundsService.dashboard = dashboard;
    this.fundsService.goal = dashboard.goal;
    return {
      fund,
      dashboard,
      goal: dashboard.goal,
    };
  }

  // need it here to handle the user manually entering a url for the wrong dashboard
  redirect(model: DashboardRouteModel) {
    RedirectToNamedDashboardRoute({
      model,
      currentUser: this.currentUser,
      transitionTo: this.router.transitionTo.bind(this),
    });
  }

  deactivate() {
    if (this.navbarSettings.importButtonRoute != null) {
      this.navbarSettings.importButtonRoute = undefined;
    }
  }
}
